import { z } from "zod";

/**
 * How a sender proves a delivery to `POST /api/hook/:workflowId_:nodeId` is theirs.
 *
 * A production WEBHOOK trigger carries a per-trigger secret. The sender presents it in
 * ONE of two forms, and either is accepted:
 *
 * - **The secret itself**, in `x-nexus-webhook-secret`. For senders that can set a
 *   static header and cannot compute anything — Zapier, Make, a hand-written `curl`.
 *   Compared in constant time.
 * - **An HMAC-SHA256 signature over the raw body**, in `x-nexus-signature`, shaped
 *   `t=<unix seconds>,v1=<hex digest>` and computed over `<t>.<raw body>`, the same
 *   shape Exa and Pipedream use. The secret never travels, and a delivery older than
 *   five minutes is refused, so a captured request cannot be replayed later.
 *
 * Both names are lowercase because Node lowercases every incoming header name, and the
 * verifier reads them by these exact keys.
 */
export const TRIGGER_WEBHOOK_SECRET_HEADER = "x-nexus-webhook-secret";
export const TRIGGER_WEBHOOK_SIGNATURE_HEADER = "x-nexus-signature";

/** The replay window for a signed delivery, in seconds, either side of the server clock. */
export const TRIGGER_WEBHOOK_SIGNATURE_TOLERANCE_SECONDS = 300;

/**
 * Path vars shared by the read and the rotate route. Both ids are opaque strings rather
 * than uuids: a node id is whatever the canvas minted, and the workflow id is checked
 * against the caller's organization by the use case, not by its spelling.
 */
export const TriggerWebhookSecretPathVarsSchema = z.object({
  workflowId: z.string().min(1),
  nodeId: z.string().min(1)
});

/**
 * What the trigger's owner needs to configure a sender.
 *
 * `secret` is `null` for a trigger published before secrets existed that has not been
 * re-published or rotated since: it has none yet, and rotating issues one. `enforced`
 * says whether an unauthenticated delivery is refused today. A trigger created after
 * secrets existed is always enforced; an older one is not until its owner opts in.
 */
export const TriggerWebhookSecretSchema = z.object({
  secret: z.string().nullable(),
  enforced: z.boolean(),
  issuedAt: z.string().nullable(),
  headerName: z.literal(TRIGGER_WEBHOOK_SECRET_HEADER),
  signatureHeaderName: z.literal(TRIGGER_WEBHOOK_SIGNATURE_HEADER),
  signatureToleranceSeconds: z.literal(TRIGGER_WEBHOOK_SIGNATURE_TOLERANCE_SECONDS)
});

export type TriggerWebhookSecret = z.infer<typeof TriggerWebhookSecretSchema>;
